import React, { useState, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, parseISO, addDays, getDay } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import TaskCard from './TaskCard';

const DAYS_OF_WEEK_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TaskCalendarView = ({ tasks, onUpdate, planStartDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDateStr, setSelectedDateStr] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(null);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getDayLabel = useCallback((week, day) => {
    if (!planStartDate) return `Day ${day}`;
    try {
      const [year, month, d] = planStartDate.split('-').map(Number);
      const baseDate = new Date(year, month - 1, d);
      const targetDate = addDays(baseDate, ((week - 1) * 7) + (day - 1));
      return `${DAYS_OF_WEEK_FULL[getDay(targetDate)]}`;
    } catch (e) {
      return `Day ${day}`;
    }
  }, [planStartDate]);

  const calculateDateFromPosition = useCallback((week, day) => {
    if (!planStartDate) return null;
    try {
      const [year, month, d_part] = planStartDate.split('-').map(Number);
      const baseDate = new Date(year, month - 1, d_part);
      return format(addDays(baseDate, ((week - 1) * 7) + (day - 1)), 'yyyy-MM-dd');
    } catch (e) {
      return null;
    }
  }, [planStartDate]);

  const handleRescheduleRestDay = async (taskId, newDayNumber) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task || !planStartDate) return;
    
    setIsRescheduling(taskId);
    try {
      const weekNumber = task.week_number;
      const targetTask = tasks.find(t => t.week_number === weekNumber && t.day_number === newDayNumber);
      
      const updates = [];
      if (targetTask) {
        updates.push({
          id: targetTask.id,
          day_number: task.day_number,
          task_date: calculateDateFromPosition(weekNumber, task.day_number)
        });
      }
      updates.push({
        id: taskId,
        day_number: newDayNumber,
        task_date: calculateDateFromPosition(weekNumber, newDayNumber)
      });

      for (const update of updates) {
        const { id, ...data } = update;
        await supabase.from('training_tasks').update(data).eq('id', id);
      }

      // Global Date Resync to prevent overlaps
      const { data: allTasks } = await supabase
        .from('training_tasks')
        .select('id, week_number, day_number')
        .eq('plan_id', task.plan_id);

      if (allTasks) {
        const resyncPromises = allTasks.map(t => {
          const correctDate = calculateDateFromPosition(t.week_number, t.day_number);
          return supabase.from('training_tasks').update({ task_date: correctDate }).eq('id', t.id);
        });
        await Promise.all(resyncPromises);
      }

      onUpdate();
    } catch (err) {
      console.error('Rescheduling failed:', err);
    } finally {
      setIsRescheduling(null);
    }
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.task_date) return false;
      return isSameDay(parseISO(task.task_date), date);
    });
  };

  const selectedDateTasks = selectedDateStr ? getTasksForDate(parseISO(selectedDateStr)) : [];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/30">
          <button 
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
            className="p-3 hover:bg-white hover:shadow-sm rounded-2xl transition-all text-gray-600"
          >
            <SafeIcon name="ChevronLeft" className="w-6 h-6" />
          </button>
          <h2 className="text-xl font-black text-gray-900 tracking-tight">{format(currentDate, 'MMMM yyyy')}</h2>
          <button 
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
            className="p-3 hover:bg-white hover:shadow-sm rounded-2xl transition-all text-gray-600"
          >
            <SafeIcon name="ChevronRight" className="w-6 h-6" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-px bg-gray-100">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="bg-gray-50 py-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">{day}</div>
          ))}
          
          {Array.from({ length: monthStart.getDay() }).map((_, i) => (
            <div key={`empty-${i}`} className="bg-white h-24 sm:h-32 opacity-50" />
          ))}

          {daysInMonth.map(day => {
            const dayTasks = getTasksForDate(day);
            const dateStr = format(day, 'yyyy-MM-dd');
            const hasTasks = dayTasks.length > 0;
            const allCompleted = hasTasks && dayTasks.every(t => t.is_completed);

            return (
              <button
                key={dateStr}
                onClick={() => hasTasks && setSelectedDateStr(dateStr)}
                disabled={!hasTasks}
                className={`group relative bg-white min-h-[90px] sm:min-h-[120px] p-2 transition-all flex flex-col items-center justify-start border-none outline-none ${hasTasks ? 'cursor-pointer hover:bg-indigo-50/50' : 'cursor-default opacity-40'}`}
              >
                <div className={`text-sm w-8 h-8 flex items-center justify-center rounded-xl font-black mb-2 transition-all ${isToday(day) ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-gray-900'}`}>
                  {format(day, 'd')}
                </div>
                {hasTasks && (
                  <div className="flex flex-col items-center gap-1 w-full px-1">
                    {allCompleted ? (
                      <div className="w-6 h-6 bg-green-100 text-green-600 rounded-lg flex items-center justify-center shadow-sm">
                        <SafeIcon name="Check" className="w-4 h-4" />
                      </div>
                    ) : (
                      <div className="flex -space-x-1">
                        {dayTasks.slice(0, 3).map((t, idx) => (
                          <div key={t.id} className={`w-2 h-2 rounded-full border-2 border-white shadow-sm ${t.is_rest_day ? 'bg-green-400' : 'bg-indigo-500'}`} />
                        ))}
                      </div>
                    )}
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-tighter opacity-0 group-hover:opacity-100 transition-opacity">
                      {dayTasks.length} {dayTasks.length === 1 ? 'Task' : 'Tasks'}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {selectedDateStr && (
          <div className="fixed inset-0 z-[10002] flex items-center justify-center p-4 sm:p-6" onClick={() => setSelectedDateStr(null)}>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/80 backdrop-blur-md" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-gray-50 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 border-b border-gray-200 bg-white flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-black text-gray-900 tracking-tight">
                    {format(parseISO(selectedDateStr), 'EEEE, MMMM do')}
                  </h3>
                  <p className="text-xs font-black text-indigo-500 uppercase tracking-widest mt-1">
                    {selectedDateTasks.length} {selectedDateTasks.length === 1 ? 'Scheduled Task' : 'Scheduled Tasks'}
                  </p>
                </div>
                <button onClick={() => setSelectedDateStr(null)} className="p-3 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-2xl transition-all active:scale-95">
                  <SafeIcon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6 sm:p-8 space-y-6">
                {selectedDateTasks.map(task => (
                  <TaskCard 
                    key={task.id} 
                    task={task} 
                    onUpdate={onUpdate}
                    onRescheduleRestDay={handleRescheduleRestDay}
                    isRescheduling={isRescheduling === task.id}
                    otherDaysInWeek={[1, 2, 3, 4, 5, 6, 7].map(d => ({ dayNumber: d, label: getDayLabel(task.week_number, d) }))}
                  />
                ))}
              </div>

              <div className="p-6 bg-white border-t border-gray-200 text-center">
                <button onClick={() => setSelectedDateStr(null)} className="px-8 py-3 bg-gray-900 text-white rounded-2xl font-black text-sm hover:bg-gray-800 transition-all shadow-xl active:scale-95">
                  Close Schedule
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TaskCalendarView;